import { useState } from 'react';
import type { EventWithItems, PublicParticipant } from '@trueup/shared';
import { formatPrice, receiptFont, systemFont } from '../lib/formatting';
import { bubbleSizes, spacing } from '../lib/theme';
import { getColorForName, getInitials } from '../lib/profileColors';

interface ClaimSummaryProps {
  event: EventWithItems;
  participant: PublicParticipant;
  myTotal: number;
  receiptImageUrl?: string | null;
}

// Receipt image modal
function ReceiptModal({ imageUrl, onClose }: { imageUrl: string; onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div className="relative max-w-2xl max-h-[90vh] overflow-auto">
        <button
          onClick={onClose}
          className="absolute top-2 right-2 w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-lg z-10"
        >
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <img
          src={imageUrl}
          alt="Receipt"
          className="max-w-full rounded-lg"
          onClick={(e) => e.stopPropagation()}
        />
      </div>
    </div>
  );
}

export default function ClaimSummary({
  event,
  participant,
  myTotal,
  receiptImageUrl,
}: ClaimSummaryProps) {
  const [showReceiptModal, setShowReceiptModal] = useState(false);

  // Calculate claimed amounts
  let claimedItemsAmount = 0;
  let itemsWithClaims = 0;

  for (const item of event.items) {
    if (item.quantity > 1) {
      const claimedUnitsForItem = item.claims.filter(c => c.unitNumber != null).length;
      if (claimedUnitsForItem > 0) {
        itemsWithClaims++;
      }
      for (const claim of item.claims) {
        if (claim.unitNumber != null) {
          claimedItemsAmount += Math.round(item.amount / item.quantity);
        }
      }
    } else {
      const totalFraction = item.claims
        .filter(c => c.unitNumber == null)
        .reduce((sum, c) => sum + (c.portionNumerator ?? 1) / (c.portionDenominator ?? 1), 0);
      if (totalFraction > 0) {
        itemsWithClaims++;
      }
      for (const claim of item.claims) {
        if (claim.unitNumber == null) {
          const fraction = (claim.portionNumerator ?? 1) / (claim.portionDenominator ?? 1);
          claimedItemsAmount += Math.round(item.amount * fraction);
        }
      }
    }
  }

  // Add proportional tax/fees to claimed amount
  const subtotal = event.subtotal || 0;
  const totalAmount = event.total || subtotal;
  const taxAndFees = totalAmount - subtotal;
  const proportionalTax = subtotal > 0 ? Math.round((claimedItemsAmount / subtotal) * taxAndFees) : 0;
  const totalClaimedAmount = claimedItemsAmount + proportionalTax;
  const claimProgress = totalAmount > 0 ? (totalClaimedAmount / totalAmount) * 100 : 0;

  // Sort participants: current user first, admin second, others after
  const sortedParticipants = [...event.participants].sort((a, b) => {
    if (a.id === participant.id) return -1;
    if (b.id === participant.id) return 1;
    if (a.id === event.adminId) return -1;
    if (b.id === event.adminId) return 1;
    return 0;
  });

  return (
    <div className="bg-white py-3">
      {/* Main content - HStack with thumbnail on left, details on right (matching iOS) */}
      <div className="flex items-start gap-3 px-4">
        {/* Receipt thumbnail on left - matching iOS ClaimSummarySection */}
        {receiptImageUrl && (
          <button
            onClick={() => setShowReceiptModal(true)}
            className="flex-shrink-0 rounded-lg overflow-hidden border border-gray-200"
            style={{ width: 60, height: 80 }}
          >
            <img
              src={receiptImageUrl}
              alt="Receipt"
              className="w-full h-full object-cover"
            />
          </button>
        )}

        {/* Right side content - matching iOS VStack */}
        <div className="flex-1 flex flex-col gap-3">
          {/* Profile bubbles row - matching iOS */}
          <div className="flex items-center">
            <div className="flex items-center" style={{ marginLeft: 0 }}>
              {sortedParticipants.slice(0, 6).map((p, index) => {
                const isAdmin = p.id === event.adminId;
                const bgColor = getColorForName(p.name);

                return (
                  <div
                    key={p.id}
                    className="relative"
                    style={{
                      zIndex: sortedParticipants.length - index,
                      marginLeft: index > 0 ? `${spacing.bubbleOverlap}px` : 0,
                    }}
                  >
                    <div
                      className="rounded-full flex items-center justify-center text-gray-800 font-bold border-2 border-white"
                      style={{
                        width: bubbleSizes.summary,
                        height: bubbleSizes.summary,
                        fontSize: bubbleSizes.summary * 0.32,
                        backgroundColor: bgColor,
                      }}
                    >
                      {getInitials(p.name)}
                    </div>
                    {isAdmin && (
                      <div
                        className="absolute text-sm"
                        style={{ bottom: -4, right: -4 }}
                      >
                        💰
                      </div>
                    )}
                  </div>
                );
              })}
              {sortedParticipants.length > 6 && (
                <div
                  className="rounded-full bg-gray-300 flex items-center justify-center text-gray-600 text-xs font-medium border-2 border-white"
                  style={{
                    width: 36,
                    height: 36,
                    marginLeft: `${spacing.bubbleOverlap}px`,
                  }}
                >
                  +{sortedParticipants.length - 6}
                </div>
              )}
            </div>

            <div className="flex-1" />

            <span className="text-xs text-gray-500" style={receiptFont}>
              {event.participants.length} people
            </span>
          </div>

          {/* Progress bar - matching iOS */}
          <div>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-green-500 rounded-full transition-all duration-300"
                style={{ width: `${Math.min(claimProgress, 100)}%` }}
              />
            </div>
            <div className="flex justify-between mt-1">
              <span className="text-xs text-gray-500" style={receiptFont}>
                {itemsWithClaims} of {event.items.length} items claimed
              </span>
              <span className="text-xs text-gray-500" style={receiptFont}>
                {formatPrice(totalClaimedAmount)} / {formatPrice(totalAmount)}
              </span>
            </div>
          </div>

          {/* Your share line - larger text with monospace amount matching drawer */}
          {myTotal > 0 && (
            <div className="flex items-center gap-1 pt-1" style={systemFont}>
              <span className="font-semibold text-gray-900" style={{ fontSize: '17px' }}>Your share of the bill is</span>
              <span className="font-bold text-blue-600" style={{ ...receiptFont, fontSize: '20px' }}>{formatPrice(myTotal)}</span>
            </div>
          )}
        </div>
      </div>

      {/* Receipt image modal */}
      {showReceiptModal && receiptImageUrl && (
        <ReceiptModal imageUrl={receiptImageUrl} onClose={() => setShowReceiptModal(false)} />
      )}
    </div>
  );
}
